import axios from 'axios'
import { VFC } from 'react'
import type { AssetReference } from '@opallabs/cassini'
import { useLinkFormatter } from '@opallabs/hooks'
import { isSameOrigin } from '@opallabs/lambda'
import { Button, DropdownMenu, Icon } from '@opallabs/lapidary'
import * as styles from './styles'

type LinkPreviewSettingsProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  asset?: AssetReference
  disableRemoveLink?: boolean
  link: string
  onRefetchLinkPreview?: () => void
  onRemoveImage?: () => void
  onRemoveLinkPreview?: () => void
  onShowAssetPicker?: () => void
  readOnly?: boolean
}

type LinkSetting = {
  title: string
  function?: () => void
}

/**
 * Gets the download URL for an image.
 *
 * For same-origin URLs, we can use the URL directly.
 * Otherwise, browser security policy means we have to download the
 * image as a blob first.
 */
const getDownloadUrl = async (url: string) => {
  if (isSameOrigin(url)) {
    return url
  }

  try {
    const res = await axios.get<Blob>(url, { responseType: 'blob' })
    return URL.createObjectURL(res.data)
  } catch {
    return url
  }
}

export const LinkPreviewSettings: VFC<LinkPreviewSettingsProps> = ({
  open,
  onOpenChange,
  asset,
  disableRemoveLink = false,
  link,
  onRefetchLinkPreview,
  onRemoveImage,
  onRemoveLinkPreview,
  onShowAssetPicker,
  readOnly = false,
}) => {
  const normalizedUrl = useLinkFormatter(link)

  const onGoToLink = () => {
    window.open(normalizedUrl)
  }

  const onDownloadImage = async () => {
    if (asset?.full_url) {
      const anchor = document.createElement('a')
      anchor.href = await getDownloadUrl(asset.full_url)
      anchor.download = asset.filename || ''

      document.body.appendChild(anchor)
      anchor.click()

      document.body.removeChild(anchor)
      // `href` may be a blob URL; revokeObjectURL will fail
      // silently if it's not a blob URL
      URL.revokeObjectURL(anchor.href)
    }
  }

  const settings: LinkSetting[] = readOnly
    ? asset
      ? [{ title: 'Download current asset', function: onDownloadImage }]
      : []
    : [
        { title: 'Go to link', function: onGoToLink },
        { title: 'Re-fetch link preview', function: onRefetchLinkPreview },
        ...(asset
          ? [
              { title: 'Remove image from link', function: onRemoveImage },
              { title: 'Download current asset', function: onDownloadImage },
            ]
          : []),
        { title: 'Add image from asset library', function: onShowAssetPicker },
        ...(!disableRemoveLink
          ? [{ title: 'Remove link preview', function: onRemoveLinkPreview }]
          : []),
      ]

  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger asChild>
        <Button
          name="Link Preview Settings"
          ariaLabel="Toggle Link Preview Settings"
          ariaPressed={open}
          icon={<Icon glyph="cog-lg" useHypernova ariaHidden />}
          hideButtonText
          styleOverrides={{ button: styles.triggerButton }}
          variant="ghost"
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          onInteractOutside={() => onOpenChange(false)}
          css={styles.menu}
          zIndex={1001} // higher zIndex for moment shroud
        >
          {settings.map((setting) => (
            <DropdownMenu.Item
              key={setting.title}
              onSelect={setting.function}
              css={styles.menuItem}
            >
              {setting.title}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
